import * as THREE from 'three';
import { LinearFilter, LinearMipMapLinearFilter, Texture, TextureLoader } from 'three';
import { EAssetType, EMaterialMapType, IAsset, IFilesApiColor, IFilesApiMaterial, IFilesApiObject, IMaterial } from '../@types/FilesAPIModels';
import { MM_TO_INTERNAL_FACTOR } from './constants';
import filesApiService from './filesApiService';

/**
 * Loads a texture by fetching the material through its product.
 * @param productId  ID of the product that “owns” this material
 * @param materialId ID of the material asset
 * @returns THREE.Texture or null on failure
 */
export const loadProductMaterialTexture = async (productId: number, materialId: number): Promise<Texture | null> => {
  try {
    const material: IMaterial | undefined = await filesApiService.getProductMaterial(productId, materialId);
    if (!material) {
      console.error(`Failed to fetch material ${materialId} for product ${productId}`);
      return null;
    }
    const maps = Array.isArray((material as any)?.maps) ? (material as any).maps : Array.isArray((material as any)?.object?.maps) ? (material as any).object.maps : [];

    const map = maps.find((m: any) => m?.type === EMaterialMapType.map) ?? null;
    if (!map) {
      console.error(`No 'map' entry found on product material ${materialId}`);
      return null;
    }
    const textureUrl = await filesApiService.getProductMaterialMapImage(productId, materialId, map.id);
    if (!textureUrl) {
      console.error(`No ‘map’ found on material ${materialId} (product ${productId})`);
      return null;
    }

    return await new Promise<Texture>((resolve, reject) => {
      const loader = new TextureLoader();
      loader.load(
        textureUrl,
        (texture) => {
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = LinearFilter;
          texture.generateMipmaps = true;
          texture.anisotropy = 1;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;

          if (texture.image && typeof material.width === 'number' && material.width > 0 && typeof material.length === 'number' && material.length > 0) {
            const { scaleX, scaleY } = calculateTextureScale(material.width, material.length);
            texture.repeat.set(scaleX, scaleY);
          } else {
            texture.repeat.set(0.1, 0.1);
          }

          resolve(texture);
        },
        undefined,
        (error) => {
          console.error(`Error loading texture for material ${materialId}:`, error, 'URL:', textureUrl);
          reject(error);
        },
      );
    });
  } catch (error) {
    console.error(`Unexpected error in loadProductMaterialTexture(${productId}, ${materialId}):`, error);
    return null;
  }
};

/**
 * Loads a texture from the given material ID and applies rotation and alignment.
 * @param materialId The ID of the material to fetch the texture for.
 * @returns A promise that resolves to the loaded THREE.Texture or null if there's an error.
 */
export const loadMaterialTexture = async (materialId: number): Promise<Texture | null> => {
  try {
    const material: IMaterial | undefined = await filesApiService.getMaterial(materialId);

    if (!material) {
      console.error(`Failed to fetch material with ID ${materialId}`);
      return null;
    }

    const textureUrl = await filesApiService.getMaterialMapImage(materialId, material);

    if (!textureUrl) {
      console.error(`Failed to fetch map image for material ID ${materialId}`);
      return null;
    }

    return new Promise((resolve, reject) => {
      const loader = new TextureLoader();

      loader.load(
        textureUrl,
        (texture) => {
          texture.minFilter = LinearMipMapLinearFilter;
          texture.magFilter = LinearFilter;
          texture.anisotropy = 1;
          texture.generateMipmaps = true;
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;

          // TODO if only length OR width exists
          if (texture.image && material.length > 0 && material.width > 0) {
            const { scaleX, scaleY } = calculateTextureScale(material.width, material.length);

            texture.repeat.set(scaleX, scaleY);
          } else {
            texture.repeat.set(0.1, 0.1);
          }

          resolve(texture);
        },
        undefined,
        (error) => {
          console.error('Error loading texture:', error, 'Texture URL:', textureUrl);
          reject(error);
        },
      );
    });
  } catch (error) {
    console.error('Error loading material texture:', error);
    return null;
  }
};

/**
 * Calculates the correct texture scaling factors based on real-world material dimensions.
 *
 * @param width - The real-world width of the material in mm.
 * @param height - The real-world height of the material in mm.
 * @returns An object containing the scale factors for X and Y axes.
 */
const calculateTextureScale = (width: number, height: number): { scaleX: number; scaleY: number } => {
  let scaleX = 1,
    scaleY = 1;

  if (width > 0 && height > 0) {
    // Convert material dimensions from mm to scene units
    const widthInUnits = width * MM_TO_INTERNAL_FACTOR;
    const heightInUnits = height * MM_TO_INTERNAL_FACTOR;

    // Compute scale factors to ensure correct texture sizing
    scaleX = 1 / widthInUnits;
    scaleY = 1 / heightInUnits;
  }

  return { scaleX, scaleY };
};

export const convertMaterialToTTexture = (asset: IAsset): IFilesApiMaterial => {
  const material = asset as unknown as IMaterial;
  return {
    id: material.id,
    ownerId: material.ownerID,
    name: material.name,
    assetType: EAssetType.MATERIAL,
    hasFullAccess: asset.hasFullAccess,
    object: material.objects as unknown as IFilesApiObject,
    length: material.length,
    width: material.width,
    previewPictureId: material.previewPicture ? material.previewPicture.id : null,
    previewPictureUrl: material.previewPicture ? material.previewPicture.thumbnailPath : '',
  };
};

// Converts an IAsset (of type color) into an IFilesApiColor (which is part of TTexture)
export const convertColorAssetToTTexture = (asset: IAsset): IFilesApiColor => ({
  id: asset.id,
  ownerId: asset.ownerId,
  name: asset.name,
  assetType: EAssetType.COLOR,
  hasFullAccess: asset.hasFullAccess,
  object: asset.object,
  hex: (asset as unknown as IFilesApiColor).hex,
  ral: (asset as unknown as IFilesApiColor).ral || null,
});
